import { ChatInput } from './ChatInput';
import { FlagTriangleRight, Landmark } from 'lucide-react';
import {
  Flex,
  Box,
  HStack,
  VStack,
  Text,
  Icon,
  Stack,
  Button,
  type AvatarProps,
  Avatar,
} from '@chakra-ui/react';
import { Fragment } from 'react';
import { Messages } from './Messages';

export function Chat({
  chatId,
  currentUserDrn,
  InputHeader,
  onMessageReceived,
}: {
  chatId: string;
  currentUserDrn: string;
  // InputHeader is a ReactElement that will be rendered above the chat input
  InputHeader?: React.ComponentType<{ chatId: string }>;
  // Called when a message from another user arrives over the subscription.
  onMessageReceived?: () => void;
}) {
  // NOTE: This component must expand to fill the available space of the parent
  //       as such the height is set to 100%.
  //       Don't set it to 100vh or similar, as that's not always the correct
  //       for where this component is used.
  return (
    <Fragment key={chatId}>
      <Flex height="100%" direction="column" justify={'space-between'} flex="1">
        <Messages
          key={`${chatId}::Messages`}
          chatId={chatId}
          currentUserDrn={currentUserDrn}
          onMessageReceived={onMessageReceived}
        />
        <ChatInput
          key={`${chatId}::ChatInput`}
          chatId={chatId}
          currentUserDrn={currentUserDrn}
          InputHeader={InputHeader}
        />
      </Flex>
    </Fragment>
  );
}

type ChatUser = {
  user: {
    id?: string | undefined;
    firstName: string;
    lastName: string;
    role?: string | undefined;
    avatar?: string | undefined;
  };
  tenant: {
    id: number;
    name: string;
  };
};

export function ChatHeader({ user, tenant }: ChatUser) {
  const gotToProfile = () => {
    let url;
    if (user.role === 'CONSULTANT') {
      url = `providers/${tenant.id}/consultants/${user.id}`;
    } else if (user.role === 'STUDENT') {
      url = `providers/${tenant.id}/students/${user.id}`;
    }

    window.location.href = `/${url}`;
  };

  return (
    <Box
      position="sticky"
      top="0"
      p={{ base: '4', md: '6' }}
      borderBottom={'1px solid'}
      borderColor={'border.default'}
    >
      <Flex align="center" justify="space-between" w="full">
        <Flex align="flex-start">
          <HStack>
            <UserAvatar
              user={{
                first_name: user.firstName,
                last_name: user.lastName,
                picture_url: user.avatar,
              }}
              size="lg"
            />
            <VStack ml="4" align="start" gap={0}>
              <Text fontWeight="bold">{`${user.firstName} ${user.lastName}`}</Text>
              <UserHeaderDetails user={user} tenant={tenant} />
            </VStack>
          </HStack>
        </Flex>
        <Flex align="flex-end" mr={8}>
          <Button variant="primary" justifyContent="start" onClick={() => gotToProfile()}>
            Go to profile
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}

function ConsultantHeader({ user, tenant }: ChatUser) {
  return (
    <>
      <Stack direction={'row'} spacing={1} mr={1}>
        <Icon as={FlagTriangleRight} color="fg.muted" boxSize="5" />
        <Text fontSize="sm" color="fg.muted">
          {user.role}
        </Text>
      </Stack>
      <Stack direction={'row'} spacing={1} mr={1}>
        <Icon as={Landmark} color="fg.muted" boxSize="5" />
        <Text fontSize="sm" color="fg.muted">
          {tenant?.name}
        </Text>
      </Stack>
    </>
  );
}

function StudentHeader({ tenant }: ChatUser) {
  return (
    <>
      <Stack direction={'row'} spacing={1} mr={1}>
        <Icon as={Landmark} color="fg.muted" boxSize="5" />
        <Text fontSize="sm" color="fg.muted">
          {tenant?.name}
        </Text>
      </Stack>
    </>
  );
}

function UserHeaderDetails({ user, tenant }: ChatUser) {
  return (
    <Flex align="center">
      {user?.role === 'CONSULTANT' ? (
        <ConsultantHeader user={user} tenant={tenant}></ConsultantHeader>
      ) : user?.role === 'STUDENT' ? (
        <StudentHeader user={user} tenant={tenant}></StudentHeader>
      ) : null}
    </Flex>
  );
}

export const UserAvatar = ({
  user,
  ...props
}: {
  user: {
    first_name?: string | null | undefined;
    last_name?: string | null | undefined;
    picture_url?: string | null | undefined;
  };
} & AvatarProps) => {
  return (
    <Avatar
      {...props}
      boxSize={props.size ? undefined : '10'}
      src={user.picture_url ?? undefined}
      name={getUserFullName(user)}
    />
  );
};

export function getUserFullName(user: {
  first_name?: string | null | undefined;
  last_name?: string | null | undefined;
  picture_url?: string | null | undefined;
}): string | undefined {
  return `${user.first_name ?? ''} ${user.last_name ?? ''}`;
}
